import React, { useEffect, useRef } from "react";
import { pickColor, rgba, smoothNoise } from "./internal/colorUtils";

export const key = "wireframe-terrain";

const DEFAULT_SKY = "#020617";
const DEFAULT_NEAR = "#22d3ee";
const DEFAULT_FAR = "#7c3aed";

// Camera flies forward over a procedurally generated heightfield. The mesh
// is drawn as a wireframe: longitudinal lines (constant x in world space)
// and lateral lines (constant z in world space). Lines fade with distance,
// so the horizon dissolves into the background.
const COLS = 28;          // grid cells across the x axis (left ↔ right)
const ROWS = 32;          // grid cells along the z axis (near ↔ far)
const CELL = 1.0;         // size of each grid cell in world units
const SCROLL_SPEED = 0.025; // world units per frame
const NOISE_SCALE = 0.6;
const HEIGHT = 1.4;       // mountain amplitude in world units
const CAMERA_HEIGHT = 1.4;
const CAMERA_PITCH = 0.18;
const FOCAL = 1.6;        // perspective focal length

function project(x, y, z, width, height) {
  // Simple pinhole camera: (x, y, z) is in world space with the camera
  // at (0, CAMERA_HEIGHT, 0) looking down +z with a small downward pitch.
  const yc = y - CAMERA_HEIGHT;
  // pitch rotates around the x axis (yc, z) → (yc', z')
  const cp = Math.cos(CAMERA_PITCH);
  const sp = Math.sin(CAMERA_PITCH);
  const yp = yc * cp - z * sp;
  const zp = yc * sp + z * cp;
  if (zp <= 0.05) return null;
  const sx = (x / zp) * FOCAL * (height / 2) + width / 2;
  const sy = (yp / zp) * FOCAL * (height / 2) + height / 2;
  return { sx, sy, depth: zp };
}

export default function WireframeTerrain({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_SKY, DEFAULT_NEAR, DEFAULT_FAR]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_SKY),
      pickColor(colors, 1, DEFAULT_NEAR),
      pickColor(colors, 2, DEFAULT_FAR),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let zOffset = 0;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    // Reused per-frame buffer for projected mesh points.
    const projected = new Array((COLS + 1) * (ROWS + 1));

    const tick = () => {
      const [sky, near, far] = colorsRef.current;

      // Sky gradient — color of "near" lifts slightly into the haze for cohesion.
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, sky);
      grad.addColorStop(0.55, sky);
      grad.addColorStop(1, rgba(far, 0.4));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // Compute world-space height for every grid vertex, then project.
      // The grid is centered around x=0 with COLS+1 vertices across.
      for (let r = 0; r <= ROWS; r += 1) {
        for (let c = 0; c <= COLS; c += 1) {
          const wx = (c - COLS / 2) * CELL;
          const wz = r * CELL + zOffset; // shift entire grid forward as offset grows
          const wy = smoothNoise(wx * NOISE_SCALE, wz * NOISE_SCALE) * HEIGHT * 0.5;
          projected[r * (COLS + 1) + c] = project(wx, wy, wz, width, height);
        }
      }

      const drawSegment = (a, b) => {
        if (!a || !b) return;
        // Mix near/far colors based on furthest depth; fade out at the horizon.
        const depth = Math.max(a.depth, b.depth);
        const fadeStart = ROWS * 0.6;
        const fadeEnd = ROWS * 0.95;
        const fade = Math.max(0, Math.min(1, 1 - (depth - fadeStart) / (fadeEnd - fadeStart)));
        if (fade <= 0.02) return;
        const mix = Math.min(1, depth / (ROWS * 0.85));
        // Lerp between near and far in RGB space (cheaply, by alpha-blending two strokes).
        ctx.strokeStyle = rgba(mix < 0.5 ? near : far, fade * 0.85);
        ctx.beginPath();
        ctx.moveTo(a.sx, a.sy);
        ctx.lineTo(b.sx, b.sy);
        ctx.stroke();
      };

      ctx.lineWidth = 1;
      // Lateral lines (constant r): connect c → c+1
      for (let r = 0; r <= ROWS; r += 1) {
        for (let c = 0; c < COLS; c += 1) {
          drawSegment(projected[r * (COLS + 1) + c], projected[r * (COLS + 1) + c + 1]);
        }
      }
      // Longitudinal lines (constant c): connect r → r+1
      for (let r = 0; r < ROWS; r += 1) {
        for (let c = 0; c <= COLS; c += 1) {
          drawSegment(projected[r * (COLS + 1) + c], projected[(r + 1) * (COLS + 1) + c]);
        }
      }

      zOffset -= SCROLL_SPEED;
      // Wrap so we never lose precision; one CELL forward looks identical
      // to zero when the terrain is built from continuous noise.
      if (zOffset < -CELL) zOffset += CELL;

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
